import React, { useLayoutEffect, useRef, useState } from 'react';

const PAD = { top: 20, right: 20, bottom: 28, left: 44 };
const DAY = 86400000;

const toTime = (date) => {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d).getTime();
};
const fmtDate = (t) => new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

// Breathing room around the data, at least 1 unit so a flat line isn't pinned to an edge.
const padRange = (lo, hi) => {
  const pad = Math.max((hi - lo) * 0.1, 1);
  return [lo - pad, hi + pad];
};

// Round-number ticks covering [lo, hi].
function niceTicks(lo, hi, count = 5) {
  const raw = (hi - lo) / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map(m => m * mag).find(s => s >= raw);
  const ticks = [];
  const last = Math.ceil(hi / step) * step;
  for (let v = Math.floor(lo / step) * step; v <= last + 1e-9; v += step) ticks.push(+v.toFixed(6));
  return ticks;
}

// One metric over time. The y-axis always includes the goal, so when the goal is
// above every logged value it is the chart's upper bound.
const MetricChart = ({ entries, metric, unit, label = metric, goal, chartHeight: HEIGHT = 280 }) => {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(600);
  const [hover, setHover] = useState(null);

  // Measure before paint so the first frame is already full width.
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    setWidth(Math.max(280, el.getBoundingClientRect().width));
    const ro = new ResizeObserver(([e]) => setWidth(Math.max(280, e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const points = entries
    .filter(e => Number.isFinite(e[metric]))
    .map(e => ({ t: toTime(e.date), w: e[metric] }));

  if (points.length === 0) {
    return <div className="fitness-chart-empty">Log a {label.toLowerCase()} measurement to start the graph.</div>;
  }

  const hasGoal = Number.isFinite(goal);
  const values = points.map(p => p.w).concat(hasGoal ? [goal] : []);
  const ticks = niceTicks(...padRange(Math.min(...values), Math.max(...values)));
  const yMin = ticks[0];
  const yMax = ticks[ticks.length - 1];

  let tMin = points[0].t;
  let tMax = points[points.length - 1].t;
  if (tMax - tMin < DAY) { tMin -= 3 * DAY; tMax += 3 * DAY; }

  const plotW = width - PAD.left - PAD.right;
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const x = (t) => PAD.left + ((t - tMin) / (tMax - tMin)) * plotW;
  const y = (w) => PAD.top + (1 - (w - yMin) / (yMax - yMin)) * plotH;

  const path = points.map((p, i) => `${i ? 'L' : 'M'}${x(p.t)},${y(p.w)}`).join('');
  const xTickCount = Math.min(points.length, Math.floor(plotW / 90));
  const xTicks = xTickCount < 2
    ? [points[0].t]
    : Array.from({ length: xTickCount }, (_, i) => tMin + (i * (tMax - tMin)) / (xTickCount - 1));

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left;
    let best = 0;
    points.forEach((p, i) => { if (Math.abs(x(p.t) - px) < Math.abs(x(points[best].t) - px)) best = i; });
    setHover(best);
  };

  const hp = hover != null ? points[hover] : null;
  const last = points[points.length - 1];

  return (
    <div className="fitness-chart" ref={wrapRef}>
      <svg width={width} height={HEIGHT} onPointerMove={onMove} onPointerLeave={() => setHover(null)}
        role="img" aria-label={`${label} over time`}>
        {ticks.map(v => (
          <g key={v}>
            <line className="fc-grid" x1={PAD.left} x2={width - PAD.right} y1={y(v)} y2={y(v)} />
            <text className="fc-axis" x={PAD.left - 8} y={y(v)} dy="0.32em" textAnchor="end">{v}</text>
          </g>
        ))}
        {xTicks.map(t => (
          <text key={t} className="fc-axis" x={x(t)} y={HEIGHT - 8} textAnchor="middle">{fmtDate(t)}</text>
        ))}
        {hasGoal && (
          <g>
            <line className="fc-goal" x1={PAD.left} x2={width - PAD.right} y1={y(goal)} y2={y(goal)} />
            <text className="fc-goal-label" x={width - PAD.right} y={y(goal) - 6} textAnchor="end">
              Goal {goal} {unit}
            </text>
          </g>
        )}
        <path d={`${path}L${x(last.t)},${PAD.top + plotH}L${x(points[0].t)},${PAD.top + plotH}Z`} className="fc-area" />
        <path d={path} className="fc-line" />
        {points.length <= 60 && points.map((p, i) => (
          <circle key={i} className="fc-dot" cx={x(p.t)} cy={y(p.w)} r={hover === i ? 5 : 3.5} />
        ))}
        {hp && <line className="fc-crosshair" x1={x(hp.t)} x2={x(hp.t)} y1={PAD.top} y2={PAD.top + plotH} />}
      </svg>
      {hp && (
        <div className="fc-tooltip" style={{
          left: Math.min(x(hp.t) + 12, width - 140),
          top: Math.max(y(hp.w) - 52, 0),
        }}>
          <div className="fc-tooltip-date">{fmtDate(hp.t)}</div>
          <div><strong>{hp.w} {unit}</strong></div>
          {hasGoal && <div className="fc-tooltip-muted">{Math.abs(goal - hp.w).toFixed(1)} {unit} from goal</div>}
        </div>
      )}
    </div>
  );
};

export default MetricChart;
